/*eslint-disable*/
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  useTable,
  useSortBy,
  useFilters,
} from "react-table";
import classnames from "classnames";
import Moment from "react-moment";
import Preloader from "./preloader";
Moment.globalFormat = 'YYYY-MM-DD HH:mm:ss';

const safeStringify = (value) => {
    try {
        if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    } catch (error) {
        return '';
    }
};

function mapRecordsToTableData(records) {
  return (records || []).map((site, key) => ({
    id: key,
    icon: (<img src={require("assets/img/thumb.ico")} alt="logo" />),
    site: site.active ? (<Link to={"/site?site_id=" + site.site_id}>{site.site_name}</Link>) :
      (<><Link to={"/site?site_id=" + site.site_id}>{site.site_name}</Link>&nbsp;&nbsp;<small>disabled (click to edit)</small></>),
    site_value: site.site_name.toLowerCase(),
    pages: site.pages,
    last_crawl: (<Moment unix>{site.last_crawl}</Moment>),
    last_crawl_value: site.last_crawl,
    last_healthy_crawl: (<Moment unix>{site.last_healthy_crawl}</Moment>),
    last_healthy_crawl_value: site.last_healthy_crawl,
    enabled: site.enabled,
    debug_info: (<>
      active: {safeStringify(site.active)}  <br />
      crawl_frequency: {safeStringify(site.crawl_frequency)}  <br />
      created: {safeStringify(site.created)}  <br />
      current_crawl: {safeStringify(site.current_crawl)}  <br />
      ignore_case: {safeStringify(site.ignore_case)}  <br />
      last_crawl: {safeStringify(site.last_crawl)}  <br />
      last_healthy_crawl: {safeStringify(site.last_healthy_crawl)}  <br />
      latency_issues: {safeStringify(site.latency_issues)}  <br />
      local_only: {safeStringify(site.local_only)}  <br />
      owner: {safeStringify(site.owner)}  <br />
      pageLimit: {safeStringify(site.pageLimit)}  <br />
      poll_frequency: {safeStringify(site.poll_frequency)}  <br />
      save_html: {safeStringify(site.save_html)}  <br />
      save_pdfs: {safeStringify(site.save_pdfs)}  <br />
      site_id: {safeStringify(site.site_id)}  <br />
      site_name: {safeStringify(site.site_name)}  <br />
      site_url: {safeStringify(site.site_url)}  <br />
      spider: {safeStringify(site.spider)}  <br />
      status: {safeStringify(site.status)}  <br />
      take_screenshot: {safeStringify(site.take_screenshot)}  <br />
      updated: {safeStringify(site.updated)}  <br />
      users: {safeStringify(site.users)}
    </>)
  })).sort((a, b) => {
    if (a.site_value < b.site_value) return -1;
    if (a.site_value > b.site_value) return 1;
    return 0;
  });
}

const debugColumn = {
  Header: "Debug Info",
  accessor: "debug_info",
  id: "debug_info",
  Cell: ({ value }) => (
    <div style={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "300px" }}>
      {value}
    </div>
  )
};

function Table({ columns: columnsProp, records, message }) {
  const includeDebug = typeof window !== 'undefined' && window.location.href.includes("debug");
  const columns = useMemo(
    () => (includeDebug ? [...columnsProp, debugColumn] : columnsProp),
    [includeDebug, columnsProp]
  );

  const data = useMemo(() => mapRecordsToTableData(records), [records]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setFilter,
  } = useTable(
    {
      columns,
      data,
      filterTypes: {
        // react-table v7 passes (rows, columnIds[], filterValue) - second param is array
        text: (rows, columnIds, filterValue) => {
          const search = (filterValue || "").toLowerCase();
          if (!search) return rows;
          const columnId = Array.isArray(columnIds) ? columnIds[0] : columnIds;
          return rows.filter((row) => {
            const val = columnId === "site" ? row.original.site_value : row.values[columnId];
            const str = typeof val === "string" ? val : "";
            return str.toLowerCase().includes(search);
          });
        },
      },
      disableSortRemove: true,
    },
    useFilters,
    useSortBy,
  );

  const handleSearchInputChange = (event) => {
    const value = event.target.value || "";
    setFilter("site", value.toLowerCase());
  };

  // No data at all: show message or loader (initial load). Don't show loader when we have data but filter returned 0 rows.
  const hasRecords = data.length > 0;
  const hasFilteredRows = rows.length > 0;
  const showEmptyDataState = !hasRecords && message;
  const showLoader = !hasRecords && !message;

  if (showLoader) {
    return <Preloader inline={true} overlay={false} />;
  }
  if (showEmptyDataState) {
    return <div className="text-center">{message}</div>;
  }

  return (
    <div className="ReactTable -striped -highlight primary-pagination dashboard-table">
      <div className="table-toolbar table-toolbar--search-right">
        <div className="table-toolbar__search">
          <label htmlFor="dashboard-sites-search" className="table-toolbar__label">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <span className="visually-hidden">Search sites</span>
          </label>
          <input
            id="dashboard-sites-search"
            type="search"
            placeholder="Search sites by name…"
            className="navbar-search-form table-toolbar__input"
            onChange={handleSearchInputChange}
            autoComplete="off"
          />
        </div>
      </div>
      <table {...getTableProps()} className="rt-table">
        <thead className="rt-thead -header">
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr">
            {headerGroup.headers.map((column, key) => (
              <th
                {...column.getHeaderProps(column.getSortByToggleProps())}
                className={classnames("rt-th text-left", {
                  "-cursor-pointer": [0, 1, 3, 4].includes(key),
                  "-sort-asc": column.isSorted && !column.isSortedDesc,
                  "-sort-desc": column.isSorted && column.isSortedDesc,
                })}
              >
                <div className="rt-resizable-header-content">
                  {column.render("Header")}
                </div>
              </th>
            ))}
          </tr>
        ))}
        </thead>
        <tbody {...getTableBodyProps()} className="rt-tbody">
        {hasFilteredRows ? (
          rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                key={row.id}
                className={classnames(
                  "rt-tr",
                  { " -odd": i % 2 === 0 },
                  { " -even": i % 2 === 1 }
                )}
              >
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} className="rt-td">
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })
        ) : (
          <tr className="rt-tr rt-tr--empty-filter">
            <td colSpan={headerGroups[0]?.headers?.length ?? 3} className="rt-td rt-td--empty-message">
              <i className="fa-solid fa-magnifying-glass me-2" aria-hidden="true" />
              No sites match your search.
            </td>
          </tr>
        )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
